/** Starter frame themes for snapshot-only sessions. They are SVG overlays, so
 * they stay crisp at kiosk and print resolutions and do not add image assets. */
export const SNAPSHOT_FRAME_PRESETS = [
  { slug: "classic", category: "Classic", name: "Black & Gold", colors: ["#d8b56a", "#fff0c2"], style: "classic" },
  { slug: "wedding", category: "Wedding", name: "Garden Vows", colors: ["#91a78d", "#f0e8d9"], style: "floral" },
  { slug: "birthday", category: "Birthday", name: "Confetti Party", colors: ["#f38a72", "#ffd76a"], style: "confetti" },
  { slug: "party", category: "Party", name: "Neon Nights", colors: ["#ef4ec8", "#48d8f0"], style: "neon" },
  { slug: "holiday", category: "Other Holidays", name: "Festive Gold", colors: ["#c7a45c", "#fff0c2"], style: "classic" },
  { slug: "christmas", category: "Christmas", name: "Winter Evergreen", colors: ["#2b805e", "#d94e4e"], style: "holiday" },
  { slug: "new_year", category: "New Year's Eve", name: "Midnight Sparkle", colors: ["#e3c269", "#f6f0d8"], style: "sparkle" },
  { slug: "halloween", category: "Halloween", name: "Spooky Night", colors: ["#f28b35", "#9d69dc"], style: "spooky" },
  { slug: "kids", category: "Kids Birthday", name: "Rainbow Pop", colors: ["#fa7187", "#63c9dc"], style: "confetti" },
  { slug: "valentines", category: "Valentine's Day", name: "Sweetheart", colors: ["#e75d82", "#ffd0d7"], style: "floral" },
] as const;

type LayoutId = "single" | "strip_2" | "strip_3" | "strip_4" | "grid_4";

/** Build a full poster frame with transparent photo windows matching the booth. */
export function snapshotFramePresetDataUrl(layoutId: string, presetSlug: string): string | null {
  const preset = SNAPSHOT_FRAME_PRESETS.find((item) => item.slug === presetSlug);
  if (!preset) return null;
  const geometry = posterGeometry(layoutId as LayoutId);
  const { width, height, margin, header, footer, gap, slotWidth, slotHeight, columns, scale } = geometry;
  const [primary, secondary] = preset.colors;
  const photoFrames = Array.from({ length: geometry.count }, (_, index) => {
    const x = margin + (index % columns) * (slotWidth + gap);
    const y = margin + header + Math.floor(index / columns) * (slotHeight + gap);
    return `<rect x="${x + 3}" y="${y + 3}" width="${slotWidth - 6}" height="${slotHeight - 6}" rx="18" fill="none" stroke="${primary}" stroke-width="12"/>`;
  }).join("");
  const photoWindows = Array.from({ length: geometry.count }, (_, index) => {
    const x = margin + (index % columns) * (slotWidth + gap);
    const y = margin + header + Math.floor(index / columns) * (slotHeight + gap);
    return `<rect x="${x}" y="${y}" width="${slotWidth}" height="${slotHeight}" fill="#000"/>`;
  }).join("");
  const ornaments = makeOrnaments(preset.style, width, height, margin, primary, secondary);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
    <defs><mask id="photo-windows" maskUnits="userSpaceOnUse"><rect width="${width}" height="${height}" fill="#fff"/>${photoWindows}</mask></defs>
    <rect width="${width}" height="${height}" fill="#f5f1e8" mask="url(#photo-windows)"/>
    <rect x="14" y="14" width="${width - 28}" height="${height - 28}" rx="36" fill="none" stroke="${primary}" stroke-width="28"/>
    <rect x="31" y="31" width="${width - 62}" height="${height - 62}" rx="25" fill="none" stroke="${secondary}" stroke-opacity=".85" stroke-width="5"/>
    ${photoFrames}${ornaments}
    <text x="${width / 2}" y="${margin + 48}" fill="#20242a" font-family="system-ui, sans-serif" font-size="${34 * scale}" font-weight="700" letter-spacing="${3 * scale}" text-anchor="middle">PHOTOBOOTH</text>
    <text x="${width / 2}" y="${height - footer / 2}" fill="#20242a" font-family="Georgia, serif" font-size="${24 * scale}" letter-spacing="${1 * scale}" text-anchor="middle">A MOMENT TO REMEMBER</text>
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function posterGeometry(id: LayoutId) {
  const count = id === "single" ? 1 : id === "strip_2" ? 2 : id === "grid_4" || id === "strip_4" ? 4 : 3;
  const columns = id === "grid_4" ? 2 : 1;
  const width = columns === 2 ? 1600 : 1000;
  const margin = 48;
  const gap = 20;
  const slotWidth = (width - margin * 2 - gap * (columns - 1)) / columns;
  // Snapshot photo windows are strict landscape 4:3, matching the live
  // camera preview and the composition renderer.
  const slotHeight = slotWidth * 3 / 4;
  const rows = Math.ceil(count / columns);
  const header = 130;
  const footer = 155;
  const height = margin + header + rows * slotHeight + (rows - 1) * gap + footer + margin;
  const scale = Math.min(1, 3800 / height);
  return { count, columns, width: Math.round(width * scale), height: Math.round(height * scale), margin: margin * scale, gap: gap * scale,
    slotWidth: slotWidth * scale, slotHeight: slotHeight * scale, header: header * scale, footer: footer * scale, rows, scale };
}

function makeOrnaments(style: string, width: number, height: number, margin: number, primary: string, secondary: string) {
  const points = [
    [margin / 2, margin / 2], [width - margin / 2, margin / 2],
    [margin / 2, height - margin / 2], [width - margin / 2, height - margin / 2],
  ];
  if (style === "confetti" || style === "neon") {
    return points.map(([x, y], i) => `<path d="M${x - 14} ${y}h28M${x} ${y - 14}v28" stroke="${i % 2 ? secondary : primary}" stroke-width="9" stroke-linecap="round"/>`).join("");
  }
  if (style === "floral") {
    return points.map(([x, y], i) => `<g transform="translate(${x} ${y})" fill="${i % 2 ? secondary : primary}" fill-opacity=".95"><circle r="7" cy="-12"/><circle r="7" cx="12"/><circle r="7" cy="12"/><circle r="7" cx="-12"/><circle r="5"/></g>`).join("");
  }
  if (style === "holiday" || style === "sparkle") {
    return points.map(([x, y]) => `<path d="M${x} ${y - 16}l5 11 11 5-11 5-5 11-5-11-11-5 11-5z" fill="${primary}" stroke="${secondary}" stroke-width="3"/>`).join("");
  }
  if (style === "spooky") {
    return points.map(([x, y], i) => `<circle cx="${x}" cy="${y}" r="12" fill="${i % 2 ? secondary : primary}"/><circle cx="${x - 4}" cy="${y - 2}" r="2" fill="#20152c"/><circle cx="${x + 4}" cy="${y - 2}" r="2" fill="#20152c"/>`).join("");
  }
  return points.map(([x, y], i) => `<circle cx="${x}" cy="${y}" r="10" fill="${i % 2 ? secondary : primary}"/>`).join("");
}
